import { FC, useRef } from 'react';
import { Formik, Form, Field, FormikProps, FieldProps } from 'formik';
import { Grid, TextareaAutosize, Box, Chip, FormHelperText } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import moment, { Moment } from 'moment';

import { AlertDialog } from 'components/AlertDialog';
import { useAdmin } from 'store/admin/hooks';
import { RejectionReasonTag } from 'core/types';
import { rejectionReasonsOptions } from 'core/constants';
import { SendAtDatePicker } from 'components/SendAtDatePicker';
import { CustomLabel } from 'components/inputs';
import useStyles from './MultiRejectModal.styles';

interface FormValues {
  rejectionReason: string | null;
  rejectionReasonTags: RejectionReasonTag[];
  send_at: Date;
}

interface MultiRejectModalProps {
  open: boolean;
  toggleOpen: () => void;
  applicationIds: string[];
}

const MultiRejectModal: FC<MultiRejectModalProps> = ({ open, toggleOpen, applicationIds }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<FormValues> | null>(null);
  const { rejectApplication, setError } = useAdmin();

  const rejectSchema = yup.object({
    rejectionReason: yup.string().nullable(),
    rejectionReasonTags: yup
      .array(yup.string())
      .min(1, t('pages.lead.admin.reject.inputs.rejectionReasonTags.error'))
      .required(t('pages.lead.admin.reject.inputs.rejectionReasonTags.required'))
      .nullable(),
    send_at: yup
      .date()
      .required('Required')
      .max(moment().add(72, 'hours').toDate(), 'Send date must be within 72 hours'),
  });

  const initialValues: FormValues = {
    rejectionReason: '',
    rejectionReasonTags: [],
    send_at: moment().toDate(),
  };

  const onSubmit = async (values: FormValues) => {
    if (!applicationIds.length) return;

    try {
      await Promise.all(
        applicationIds.map((id) =>
          rejectApplication({
            id,
            rejectionReason: values.rejectionReason ?? undefined,
            rejectionReasonTags: values.rejectionReasonTags,
            send_at: values.send_at,
          }),
        ),
      );

      formRef.current?.resetForm();
      toggleOpen();
    } catch (error) {
      setError((error as Error).message ?? true);
    }
  };

  const onCancel = () => {
    formRef.current?.resetForm();
    toggleOpen();
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={rejectSchema}
      innerRef={formRef}
      enableReinitialize
    >
      {({ values, touched, errors, setFieldValue }) => (
        <AlertDialog
          open={open}
          dialogContentTitle={t('pages.lead.admin.reject.title')}
          handleCancel={onCancel}
          // ✅ Submit form on Confirm button
          handleConfirm={() => formRef.current?.submitForm()}
        >
          <Form noValidate>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <CustomLabel title={t('pages.lead.admin.reject.inputs.rejectionReasonTags.label')} />
                <Box className={classes.rejectionReasonsContainer}>
                  {rejectionReasonsOptions.map((option) => {
                    const alreadySelected = values.rejectionReasonTags.includes(option.value);
                    return (
                      <Chip
                        id={option.value}
                        key={option.value}
                        color={alreadySelected ? 'primary' : 'default'}
                        label={option.label}
                        onClick={() => {
                          if (alreadySelected) {
                            setFieldValue(
                              'rejectionReasonTags',
                              values.rejectionReasonTags.filter((v) => v !== option.value),
                            );
                          } else {
                            setFieldValue('rejectionReasonTags', [...values.rejectionReasonTags, option.value]);
                          }
                        }}
                        className={classes.rejectionReasonOption}
                      />
                    );
                  })}
                </Box>
                {touched.rejectionReasonTags && errors.rejectionReasonTags && (
                  <FormHelperText error>{errors.rejectionReasonTags}</FormHelperText>
                )}
              </Grid>

              <Grid item>
                <CustomLabel title={t('pages.lead.admin.reject.inputs.rejectionReason.label')} />
                <Field name="rejectionReason">
                  {({ field, meta }: FieldProps) => (
                    <>
                      <TextareaAutosize
                        {...field}
                        id="rejectionReason"
                        aria-label="minimum height"
                        minRows={5}
                        className={meta.touched && meta.error ? classes.textAreaError : ''}
                      />
                      {meta.touched && meta.error && <FormHelperText error>{meta.error}</FormHelperText>}
                    </>
                  )}
                </Field>
              </Grid>

              <Grid item>
                <Field name="send_at">
                  {({ field, form, meta }: FieldProps) => (
                    <SendAtDatePicker
                      title={t('pages.lead.admin.sendEmail.inputs.send_at.label')}
                      field={field}
                      form={form}
                      meta={meta}
                      onChange={(date: Moment | null) => setFieldValue('send_at', date ?? null)}
                    />
                  )}
                </Field>
              </Grid>
            </Grid>
          </Form>
        </AlertDialog>
      )}
    </Formik>
  );
};

export default MultiRejectModal;
